import {
  DepositStageStatus,
  DepositStageView,
  DepositStepperAlertVariant,
  DepositStepperCopyCtx,
  DepositStepperFlow,
  DepositSubStepStatus,
  DepositSubStepView,
  FLOW_COPY,
  resolveDepositStepperCopy,
  STAGE_STATUS_LABEL,
  STAGES_BY_FLOW,
  STEP_COPY,
  STEP_STATUS_TO_SUB,
  SWAP_SKIPPED_COMPLETED_LABEL,
  subStepStatusLabel,
} from '@/app/components/Modal/depositStepper';
import { SequentialStepView } from '@/app/components/Modal/SequentialStepModal';
import { StepStatus } from '@/app/components/Modal/StepIcon';

export { DepositStepperFlow } from '@/app/components/Modal/depositStepper';

const mapStepStatus = (status: StepStatus, unlocked: boolean): DepositSubStepStatus => {
  const mapped = STEP_STATUS_TO_SUB[status];
  if (mapped) return mapped;
  if (!unlocked) return DepositSubStepStatus.Locked;
  return DepositSubStepStatus.Ready;
};

const aggregateSubStepStatus = (statuses: DepositSubStepStatus[]): DepositSubStepStatus => {
  if (statuses.length === 0) return DepositSubStepStatus.Locked;
  if (statuses.every((status) => status === DepositSubStepStatus.Success)) {
    return DepositSubStepStatus.Success;
  }
  if (statuses.some((status) => status === DepositSubStepStatus.Error)) {
    return DepositSubStepStatus.Error;
  }
  if (statuses.some((status) => status === DepositSubStepStatus.Loading)) {
    return DepositSubStepStatus.Loading;
  }
  if (statuses.some((status) => status === DepositSubStepStatus.AwaitingWallet)) {
    return DepositSubStepStatus.AwaitingWallet;
  }
  if (statuses.some((status) => status === DepositSubStepStatus.Ready)) {
    return DepositSubStepStatus.Ready;
  }
  return DepositSubStepStatus.Locked;
};

const stageMeta = (
  subSteps: DepositSubStepView[],
  completedLabel: string,
): { status: DepositStageStatus; statusLabel: string } => {
  if (subSteps.every((step) => step.status === DepositSubStepStatus.Success)) {
    return { status: DepositStageStatus.Completed, statusLabel: completedLabel };
  }
  if (
    subSteps.some(
      (step) =>
        step.status === DepositSubStepStatus.Ready ||
        step.status === DepositSubStepStatus.AwaitingWallet ||
        step.status === DepositSubStepStatus.Loading ||
        step.status === DepositSubStepStatus.Error,
    )
  ) {
    return {
      status: DepositStageStatus.Current,
      statusLabel: STAGE_STATUS_LABEL[DepositStageStatus.Current],
    };
  }
  return {
    status: DepositStageStatus.Pending,
    statusLabel: STAGE_STATUS_LABEL[DepositStageStatus.Pending],
  };
};

const findActiveSubStep = (stages: DepositStageView[]) => {
  for (const stage of stages) {
    const active = stage.subSteps.find(
      (step) =>
        step.status === DepositSubStepStatus.Ready ||
        step.status === DepositSubStepStatus.AwaitingWallet ||
        step.status === DepositSubStepStatus.Loading ||
        step.status === DepositSubStepStatus.Error,
    );
    if (active) return { stage, active };
  }
  return null;
};

export type MapDepositStepperOptions = {
  trancheLabel: string;
  flow?: DepositStepperFlow;
  forceComplete?: boolean;
  errorMessage?: string;
  isUserRejectedError?: boolean;
  swapSkipped?: boolean;
};

const toCtx = (options: MapDepositStepperOptions): DepositStepperCopyCtx => ({
  trancheLabel: options.trancheLabel,
});

export const isDepositStepperComplete = (stages: DepositStageView[]): boolean =>
  stages.length > 0 && stages.every((stage) => stage.status === DepositStageStatus.Completed);

const isFlowComplete = (stages: DepositStageView[], options: MapDepositStepperOptions) =>
  options.forceComplete === true || isDepositStepperComplete(stages);

export const mapDepositStepperStages = (
  steps: SequentialStepView[],
  options: MapDepositStepperOptions,
): DepositStageView[] => {
  const byId = new Map(steps.map((step) => [step.id, step]));
  const ctx = toCtx(options);
  const flow = options.flow ?? DepositStepperFlow.SwapNoBatch;
  const stageConfigs = STAGES_BY_FLOW[flow];
  const statusLabelOptions =
    options.isUserRejectedError === undefined
      ? undefined
      : { isUserRejected: options.isUserRejectedError };

  let previousStageDone = true;

  return stageConfigs.map((config) => {
    let unlocked = previousStageDone;
    const underlyingSteps: DepositSubStepView[] = config.stepIds.map((stepId) => {
      const step = byId.get(stepId);
      const copy = STEP_COPY[stepId];
      const status = mapStepStatus(step?.status ?? StepStatus.Pending, unlocked);
      unlocked = status === DepositSubStepStatus.Success;

      return {
        id: stepId,
        title: copy ? resolveDepositStepperCopy(copy.title, ctx) : (step?.title ?? stepId),
        status,
        statusLabel:
          copy?.statusLabel?.[status] ?? subStepStatusLabel(status, statusLabelOptions),
      };
    });

    const subSteps: DepositSubStepView[] = (() => {
      if (!config.uiStepId) return underlyingSteps;

      const status = previousStageDone
        ? aggregateSubStepStatus(underlyingSteps.map((step) => step.status))
        : DepositSubStepStatus.Locked;
      const copy = STEP_COPY[config.uiStepId];

      return [
        {
          id: config.uiStepId,
          title: copy
            ? resolveDepositStepperCopy(copy.title, ctx)
            : resolveDepositStepperCopy(config.title, ctx),
          status,
          statusLabel: subStepStatusLabel(status, statusLabelOptions),
        },
      ];
    })();

    const completedLabel =
      config.id === 'usdc-wylds' && options.swapSkipped
        ? SWAP_SKIPPED_COMPLETED_LABEL
        : config.completedLabel;
    const { status, statusLabel } = stageMeta(subSteps, completedLabel);
    previousStageDone = status === DepositStageStatus.Completed;

    return {
      id: config.id,
      title: resolveDepositStepperCopy(config.title, ctx),
      status,
      statusLabel,
      subSteps,
    };
  });
};

export const getDepositStepperPrimary = (
  stages: DepositStageView[],
  options: MapDepositStepperOptions,
): { label: string } => {
  if (isFlowComplete(stages, options)) {
    return { label: FLOW_COPY.completePrimary };
  }

  const found = findActiveSubStep(stages);
  if (!found) return { label: FLOW_COPY.continuePrimary };

  const { active } = found;
  const copy = STEP_COPY[active.id];
  if (!copy) return { label: active.title };

  if (active.status === DepositSubStepStatus.Loading) {
    return { label: copy.primary[DepositSubStepStatus.Loading] };
  }
  if (active.status === DepositSubStepStatus.Error) {
    return { label: copy.primary[DepositSubStepStatus.Error] };
  }
  return { label: resolveDepositStepperCopy(copy.title, toCtx(options)) };
};

export const getDepositStepperAlert = (
  stages: DepositStageView[],
  options: MapDepositStepperOptions,
): { message: string; variant: DepositStepperAlertVariant } => {
  const ctx = toCtx(options);

  if (isFlowComplete(stages, options)) {
    return {
      variant: DepositStepperAlertVariant.Info,
      message: FLOW_COPY.completeAlert(ctx),
    };
  }

  const found = findActiveSubStep(stages);
  if (!found) {
    return {
      variant: DepositStepperAlertVariant.Info,
      message: FLOW_COPY.fallbackAlert,
    };
  }

  const { active } = found;
  const copy = STEP_COPY[active.id];
  if (!copy) {
    return {
      variant: DepositStepperAlertVariant.Info,
      message: FLOW_COPY.fallbackAlert,
    };
  }

  if (active.status === DepositSubStepStatus.Error) {
    const appErrorMessage =
      options.isUserRejectedError === false && options.errorMessage?.trim()
        ? options.errorMessage.trim()
        : null;

    return {
      variant: DepositStepperAlertVariant.Error,
      message:
        appErrorMessage ??
        resolveDepositStepperCopy(copy.alert[DepositSubStepStatus.Error], ctx),
    };
  }

  if (active.status === DepositSubStepStatus.Loading) {
    return {
      variant: DepositStepperAlertVariant.Info,
      message: resolveDepositStepperCopy(copy.alert[DepositSubStepStatus.Loading], ctx),
    };
  }

  if (active.status === DepositSubStepStatus.AwaitingWallet) {
    return {
      variant: DepositStepperAlertVariant.Info,
      message: resolveDepositStepperCopy(
        copy.alert[DepositSubStepStatus.AwaitingWallet] ??
          copy.alert[DepositSubStepStatus.Ready],
        ctx,
      ),
    };
  }

  return {
    variant: DepositStepperAlertVariant.Info,
    message: resolveDepositStepperCopy(copy.alert[DepositSubStepStatus.Ready], ctx),
  };
};
